// ============================================================
// ColourPicker.tsx — the ONE colour picker, app-wide.
//
// A popover shell (ColourPopover) + two panels:
//   • FreeColourPanel — any colour: a saturation/brightness area (react-
//     colorful, restyled in index.css `.casevo-colour`), a hue slider with an
//     EYEDROPPER button on its left (the browser EyeDropper API — hidden where
//     unsupported), a preview swatch + hex input (validates as you type, Enter
//     applies) and one row of Recent colours (up to 8, shared app-wide —
//     useRecentColours). Optional "Default colour" (the rich-text editor).
//   • PresetPanel — a fixed set (timeline colours, avatar colours): the same
//     shell + swatch look, no colour area.
//
// Used by: the rich-text colour button (RichTextEditor — every editor,
// notes included), the document colour + secondary colour (Admin ›
// Branding — DocumentColourPicker), timeline colours (ActionColourAccordion
// — Admin + Settings) and the avatar colour (AvatarChooserModal) —
// PresetColourPicker.
//
// Two ways to apply:
//   • live (documents) — onChange on every move, so the preview follows the drag;
//   • on release (editor) — onChange once per pointer release / Enter /
//     eyedropper / swatch pick, so one change is one undo step.
//
// Shell: rendered in a PORTAL (never clipped by a modal's overflow) at
// z-[200] — the context-menu layer: above every modal (ModalFrame z-[70] …
// ConfirmDialogue z-[95]), below toasts (z-[210]+); placed under its
// trigger and flipped above / nudged sideways to stay on screen; follows
// scroll + resize. Escape or a click outside closes it and focus returns to
// the trigger; Tab past either end closes it the same way (so focus never
// falls out of a host modal). Opens with a ~140ms fade/scale
// (animate-pop-in; none under prefers-reduced-motion).
//
// Look: the app's popover surface (menuUi MENU_SURFACE / MyWork POP — 12px
// radius, 1px casevo-border, the shared popover shadow), billing-modal input
// styling (billingUi INPUT), POP_HEAD section labels, casevo-* tokens only.
// ============================================================
import React, { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HexColorPicker } from 'react-colorful'
import { useRecentColours } from '../../store/useRecentColours'

// ── Colour helpers ─────────────────────────────────────────────
// '#abc' / 'abc' / '#aabbcc' / 'aabbcc' / 'rgb(r, g, b)' → '#aabbcc'; else null.
export const toHex = (v: string | null | undefined): string | null => {
  const s = (v ?? '').trim().toLowerCase()
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/.exec(s)
  if (m) return `#${m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1]}`
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/.exec(s)
  if (rgb) return `#${rgb.slice(1, 4).map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('')}`
  return null
}
const same = (a: string | null | undefined, b: string | null | undefined): boolean => !!a && !!b && toHex(a) === toHex(b)

// The browser EyeDropper API (Chromium 95+). Absent elsewhere → no button.
interface EyeDropperLike { open: (o?: { signal?: AbortSignal }) => Promise<{ sRGBHex: string }> }
const EyeDropperCtor = (): (new () => EyeDropperLike) | null =>
  typeof window !== 'undefined' ? ((window as unknown as { EyeDropper?: new () => EyeDropperLike }).EyeDropper ?? null) : null

// ── Shared styling ─────────────────────────────────────────────
const SURFACE =
  'bg-casevo-surface border border-casevo-border rounded-[12px] text-casevo-forest text-[13px] leading-[normal] ' +
  'shadow-[0_18px_44px_rgba(10,25,17,0.20),0_2px_6px_rgba(10,25,17,0.08)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.3)]'
const HEAD = 'text-[10.5px] tracking-[0.16em] uppercase text-casevo-text-soft font-semibold mb-[7px]'
const ICON_BTN =
  'grid place-items-center w-[28px] h-[28px] rounded-[8px] border border-casevo-border bg-casevo-surface text-casevo-mid ' +
  'hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus'
const SWATCH_RING = 'ring-2 ring-casevo-brand ring-offset-2 ring-offset-casevo-surface'
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

const Icon: React.FC<{ d: React.ReactNode; size?: number }> = ({ d, size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
)
const PIPETTE = <><path d="m2 22 1-1h3l9-9" /><path d="M3 21v-3l9-9" /><path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z" /></>
const SLASH = <><circle cx="12" cy="12" r="9" /><path d="m5.7 5.7 12.6 12.6" /></>
const CHEVRON = <polyline points="6 9 12 15 18 9" />

// ── The popover shell ──────────────────────────────────────────
interface ColourPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  label: string                       // the dialog's accessible name
  width?: number
  // Where focus goes on close (default: the trigger).
  restoreFocus?: () => void
  children: React.ReactNode
}
export const ColourPopover: React.FC<ColourPopoverProps> = ({ anchorRef, open, onClose, label, width = 240, restoreFocus, children }) => {
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null)
  const refocus = useCallback(() => { if (restoreFocus) restoreFocus(); else anchorRef.current?.focus() }, [anchorRef, restoreFocus])
  const dismiss = useCallback((returnFocus: boolean) => { onClose(); if (returnFocus) refocus() }, [onClose, refocus])

  // Place under the trigger; flip above if it would run off the bottom (and
  // there's more room above); keep 8px inside the viewport sideways.
  const place = useCallback(() => {
    const a = anchorRef.current?.getBoundingClientRect()
    const p = popRef.current
    if (!a || !p) return
    const h = p.offsetHeight
    const gap = 6; const m = 8
    const below = a.bottom + gap + h <= window.innerHeight - m
    const above = !below && a.top - gap - h >= m ? true : !below && a.top > window.innerHeight - a.bottom
    const top = above ? Math.max(m, a.top - gap - h) : Math.min(a.bottom + gap, Math.max(m, window.innerHeight - m - h))
    const left = Math.min(Math.max(m, a.left), Math.max(m, window.innerWidth - m - width))
    setPos({ top, left, above })
  }, [anchorRef, width])
  useLayoutEffect(() => { if (open) place(); else setPos(null) }, [open, place])
  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  }, [open, place])

  // First focus: the first control (the colour area, or the selected preset).
  useEffect(() => {
    if (!open || !pos) return
    const el = popRef.current?.querySelector<HTMLElement>(FOCUSABLE)
    el?.focus({ preventScroll: true })
    // Only when it first opens / is first placed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pos === null])

  // A press outside (not on the trigger, which toggles itself) closes it;
  // focus goes back to the trigger unless the press focused something else.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent): void => {
      const t = e.target as Node
      if (popRef.current?.contains(t) || anchorRef.current?.contains(t)) return
      onClose()
      window.setTimeout(() => { const ae = document.activeElement; if (!ae || ae === document.body) refocus() }, 0)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [open, onClose, anchorRef, refocus])

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); dismiss(true); return }
    if (e.key !== 'Tab') return
    const els = Array.from(popRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    if (!els.length) return
    const first = els[0]; const last = els[els.length - 1]
    if ((e.shiftKey && document.activeElement === first) || (!e.shiftKey && document.activeElement === last)) {
      e.preventDefault(); e.stopPropagation(); dismiss(true)
    }
  }

  if (!open || typeof document === 'undefined') return null
  return createPortal(
    <div
      ref={popRef}
      role="dialog"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={`fixed z-[200] p-[12px] ${SURFACE} ${pos ? 'animate-pop-in motion-reduce:animate-none' : ''}`}
      style={{ width, top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden', transformOrigin: pos?.above ? 'bottom left' : 'top left' }}
    >
      {children}
    </div>,
    document.body,
  )
}

// ── Recent colours row ─────────────────────────────────────────
const RecentRow: React.FC<{ selected: string | null; onPick: (hex: string) => void }> = ({ selected, onPick }) => {
  const recent = useRecentColours((s) => s.recent)
  if (!recent.length) return null
  return (
    <div className="mt-[12px]">
      <div className={HEAD} aria-hidden="true">Recent</div>
      <div className="flex items-center gap-[6px]" role="group" aria-label="Recent colours">
        {recent.slice(0, 8).map((c) => {
          const on = same(c, selected)
          return (
            <button key={c} type="button" onClick={() => onPick(toHex(c) ?? c)} aria-pressed={on}
              aria-label={`Recent colour ${(toHex(c) ?? c).toUpperCase()}`} title={(toHex(c) ?? c).toUpperCase()}
              className={`w-[20px] h-[20px] shrink-0 rounded-full border border-black/10 dark:border-white/15 transition-transform focus:outline-none casevo-focus ${on ? SWATCH_RING : 'hover:scale-110'}`}
              style={{ backgroundColor: c }} />
          )
        })}
      </div>
    </div>
  )
}

// ── Free colour panel ──────────────────────────────────────────
interface FreeColourPanelProps {
  value: string | null              // the current colour (null = none / default)
  fallback?: string                 // where the controls start when value is null
  live?: boolean                    // onChange on every move (else on release / Enter)
  onChange: (hex: string) => void   // '#rrggbb'
  onDone: () => void                // close the popover (Enter, editor swatch picks)
  closeOnPick?: boolean             // a Recent swatch / eyedropper pick closes it
  onDefault?: () => void            // "Default colour" (remove the colour)
}
export const FreeColourPanel: React.FC<FreeColourPanelProps> = ({ value, fallback = '#000000', live = false, onChange, onDone, closeOnPick = false, onDefault }) => {
  const pushColour = useRecentColours((s) => s.pushColour)
  const start = toHex(value) ?? toHex(fallback) ?? '#000000'
  const [colour, setColour] = useState(start)
  const [text, setText] = useState(start.toUpperCase())
  const colourRef = useRef(start)
  const initial = useRef<string | null>(toHex(value))
  const committed = useRef<string | null>(toHex(value))
  const pushed = useRef<string | null>(toHex(value))
  const dragging = useRef(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const hexId = useId()
  const hexErrId = useId()
  const Dropper = EyeDropperCtor()

  // Follow the value when it changes from outside (not mid-drag).
  useEffect(() => {
    const h = toHex(value)
    if (!h || dragging.current || h === colourRef.current) return
    colourRef.current = h; setColour(h); setText(h.toUpperCase()); committed.current = h
  }, [value])

  // Clearer names for react-colorful's two sliders (its own say "Color" / "Hue").
  useEffect(() => {
    const els = pickerRef.current?.querySelectorAll<HTMLElement>('.react-colorful__interactive')
    els?.[0]?.setAttribute('aria-label', 'Saturation and brightness — arrow keys move')
    els?.[1]?.setAttribute('aria-label', 'Hue — arrow keys move')
  }, [])

  const show = (hex: string): void => {
    colourRef.current = hex; setColour(hex); setText(hex.toUpperCase())
    if (live) onChange(hex)
  }
  // Apply (once per change) + remember in Recent.
  const commit = (hex: string): void => {
    if (hex !== committed.current) onChange(hex)
    committed.current = hex
    if (hex !== pushed.current) { pushColour(hex); pushed.current = hex }
  }
  // Live: a colour reached with the keyboard (already applied, step by step)
  // joins Recent when the popover closes.
  useEffect(() => () => { const c = colourRef.current; if (live && c !== initial.current && c !== pushed.current) pushColour(c) }, [live, pushColour])

  // A drag ends wherever the pointer is released.
  useEffect(() => {
    const up = (): void => { if (dragging.current) { dragging.current = false; commit(colourRef.current) } }
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => { window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up) }
  })

  const pick = (hex: string): void => { show(hex); commit(hex); if (closeOnPick) onDone() }
  const eyedrop = async (): Promise<void> => {
    if (!Dropper) return
    try {
      const r = await new Dropper().open()
      const h = toHex(r.sRGBHex)
      if (h) pick(h)
    } catch { /* cancelled (Escape) — nothing changes */ }
  }
  const textHex = toHex(text)
  const textInvalid = text.trim() !== '' && !textHex

  return (
    <div>
      <div
        ref={pickerRef}
        className={`casevo-colour relative ${Dropper ? '' : 'no-eyedropper'}`}
        onPointerDown={(e) => { if ((e.target as HTMLElement).closest('.react-colorful')) dragging.current = true }}
        // Arrow keys step the handle / slider (react-colorful); Enter applies + closes.
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).closest('.react-colorful')) { e.preventDefault(); commit(colourRef.current); onDone() } }}
      >
        <HexColorPicker color={colour} onChange={(h) => show(h)} />
        {Dropper && (
          <button type="button" onClick={() => { void eyedrop() }} className={`${ICON_BTN} absolute left-0 bottom-0`}
            aria-label="Pick a colour from the screen (eyedropper)" title="Eyedropper">
            <Icon d={PIPETTE} size={14} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-[8px] mt-[12px]">
        <span aria-hidden="true" className="w-[32px] h-[32px] shrink-0 rounded-[8px] border border-black/10 dark:border-white/15" style={{ backgroundColor: colour }} />
        <label htmlFor={hexId} className="sr-only">Hex colour code</label>
        <input
          id={hexId}
          value={text}
          onChange={(e) => {
            const v = e.target.value.slice(0, 7)
            setText(v)
            const h = toHex(v)
            if (h && /^#?[0-9a-f]{6}$/i.test(v.trim())) { colourRef.current = h; setColour(h); if (live) onChange(h) }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (textHex) { show(textHex); commit(textHex); onDone() } } }}
          onBlur={() => { if (textHex) setText(textHex.toUpperCase()) }}
          spellCheck={false}
          autoComplete="off"
          maxLength={7}
          aria-invalid={textInvalid || undefined}
          aria-describedby={textInvalid ? hexErrId : undefined}
          className={`h-[32px] min-w-0 flex-1 rounded-[9px] border bg-casevo-input px-[10px] font-mono text-[13px] uppercase text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none focus:bg-casevo-surface focus:ring-[3px] ${
            textInvalid ? 'border-casevo-danger focus:ring-casevo-danger/15' : 'border-casevo-border focus:border-casevo-brand focus:ring-casevo-brand/15'}`}
        />
      </div>
      {textInvalid && <p id={hexErrId} className="m-0 mt-[5px] text-[11.5px] text-casevo-danger">Use 3 or 6 hex digits, e.g. #2D6A4F.</p>}

      <RecentRow selected={colour} onPick={pick} />

      {onDefault && (
        <button type="button" onClick={() => { onDefault(); onDone() }} aria-pressed={value == null}
          className="mt-[10px] -mx-[4px] w-[calc(100%+8px)] flex items-center gap-[8px] px-[6px] py-[6px] rounded-[8px] text-[12.5px] text-casevo-mid hover:bg-casevo-input hover:text-casevo-forest transition-colors focus:outline-none casevo-focus">
          <Icon d={SLASH} size={14} />
          <span>Default colour</span>
          {value == null && <span className="ml-auto text-[11px] text-casevo-text-soft">In use</span>}
        </button>
      )}
    </div>
  )
}

// ── Preset panel ───────────────────────────────────────────────
export interface PresetSwatch { value: string; name: string; hex?: string; className?: string }
export const PresetPanel: React.FC<{ label: string; swatches: readonly PresetSwatch[]; value: string | null; onPick: (value: string) => void }> = ({ label, swatches, value, onPick }) => {
  const selected = swatches.findIndex((s) => s.value === value || same(s.value, value))
  const [focusIdx, setFocusIdx] = useState(selected >= 0 ? selected : 0)
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const cols = 6
  const move = (to: number): void => {
    const n = swatches.length
    const i = Math.max(0, Math.min(n - 1, to))
    setFocusIdx(i); refs.current[i]?.focus()
  }
  const onKeyDown = (e: React.KeyboardEvent): void => {
    const k: Record<string, number> = { ArrowRight: focusIdx + 1, ArrowLeft: focusIdx - 1, ArrowDown: focusIdx + cols, ArrowUp: focusIdx - cols, Home: 0, End: swatches.length - 1 }
    if (e.key in k) { e.preventDefault(); move(k[e.key]) }
  }
  const shown = swatches[focusIdx] ?? swatches[selected]
  return (
    <div>
      <div className={HEAD} aria-hidden="true">{label}</div>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-6 gap-[10px] justify-items-center" onKeyDown={onKeyDown}>
        {swatches.map((s, i) => {
          const on = i === selected
          return (
            <button key={s.value} ref={(el) => { refs.current[i] = el }} type="button" role="radio" aria-checked={on}
              aria-label={s.name} title={s.name} tabIndex={i === focusIdx ? 0 : -1}
              onFocus={() => setFocusIdx(i)} onClick={() => onPick(s.value)}
              className={`w-[26px] h-[26px] rounded-full border border-black/10 dark:border-white/15 transition-transform focus:outline-none casevo-focus ${s.className ?? ''} ${on ? SWATCH_RING : 'hover:scale-110'}`}
              style={s.hex ? { backgroundColor: s.hex } : undefined} />
          )
        })}
      </div>
      {shown && <div className="mt-[10px] text-[12px] text-casevo-text-soft" aria-hidden="true">{shown.name}{swatches[selected] === shown ? ' · selected' : ''}</div>}
    </div>
  )
}

// ── Trigger: a swatch + its hex / name ─────────────────────────
interface ColourTriggerProps {
  colour?: string | null      // CSS colour of the swatch (null = none)
  swatchClassName?: string    // …or a literal Tailwind fill class (presets)
  text: string                // hex / name / "None"
  mono?: boolean
  open: boolean
  onClick: () => void
  ariaLabel: string
  id?: string
  invalid?: boolean
  describedBy?: string
}
export const ColourTrigger = forwardRef<HTMLButtonElement, ColourTriggerProps>(({ colour, swatchClassName, text, mono, open, onClick, ariaLabel, id, invalid, describedBy }, ref) => {
  const empty = !colour && !swatchClassName
  return (
    // An error is shown by the red border and announced through aria-describedby
    // (aria-invalid isn't allowed on a button).
    <button ref={ref} id={id} type="button" onClick={onClick} aria-haspopup="dialog" aria-expanded={open} aria-label={ariaLabel}
      aria-describedby={describedBy}
      className={`inline-flex items-center gap-[9px] h-[38px] pl-[6px] pr-[10px] rounded-[9px] border text-[14px] text-casevo-forest transition-colors focus:outline-none casevo-focus ${
        invalid ? 'border-casevo-danger' : open ? 'border-casevo-brand bg-casevo-surface' : 'border-casevo-border bg-casevo-input hover:border-casevo-mid'}`}>
      <span aria-hidden="true"
        className={`w-[26px] h-[26px] shrink-0 rounded-[7px] ${empty ? 'border border-dashed border-casevo-text-soft bg-[linear-gradient(135deg,transparent_46%,var(--casevo-text-soft)_46%,var(--casevo-text-soft)_54%,transparent_54%)]' : `border border-black/10 dark:border-white/15 ${swatchClassName ?? ''}`}`}
        style={colour ? { backgroundColor: colour } : undefined} />
      <span className={`min-w-[64px] text-left ${mono ? 'font-mono uppercase text-[13.5px]' : ''} ${empty ? 'text-casevo-text-soft' : ''}`}>{text}</span>
      <span className={`text-casevo-mid transition-transform ${open ? 'rotate-180' : ''}`}><Icon d={CHEVRON} size={14} /></span>
    </button>
  )
})
ColourTrigger.displayName = 'ColourTrigger'

// ── Ready-made pickers ─────────────────────────────────────────
// Document colours (Admin › Branding): live — the preview follows
// the drag. value '' = none (the secondary colour) — the trigger says `emptyText`.
export const DocumentColourPicker: React.FC<{
  id?: string
  label: string               // e.g. "Document colour"
  value: string               // '#RRGGBB' / ''
  fallback: string            // where the controls start when value is ''
  onChange: (hex: string) => void
  emptyText?: string
  invalid?: boolean
  describedBy?: string
}> = ({ id, label, value, fallback, onChange, emptyText = 'None', invalid, describedBy }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const hex = toHex(value)
  return (
    <>
      <ColourTrigger ref={ref} id={id} colour={hex} text={hex ? hex.toUpperCase() : emptyText} mono={!!hex} open={open} onClick={() => setOpen((o) => !o)}
        ariaLabel={`${label}: ${hex ? hex.toUpperCase() : emptyText} — change`} invalid={invalid} describedBy={describedBy} />
      <ColourPopover anchorRef={ref} open={open} onClose={() => setOpen(false)} label={label}>
        <FreeColourPanel value={hex} fallback={fallback} live onChange={(h) => onChange(h.toUpperCase())} onDone={() => { setOpen(false); ref.current?.focus() }} />
      </ColourPopover>
    </>
  )
}

// Preset pickers (timeline / avatar): the same shell, the fixed set only.
export const PresetColourPicker: React.FC<{
  label: string
  value: string
  swatches: readonly PresetSwatch[]
  onPick: (value: string) => void
}> = ({ label, value, swatches, onPick }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const cur = swatches.find((s) => s.value === value || same(s.value, value))
  return (
    <>
      <ColourTrigger ref={ref} colour={cur?.hex ?? (cur?.className ? null : value)} swatchClassName={cur?.className} text={cur?.name ?? value}
        open={open} onClick={() => setOpen((o) => !o)} ariaLabel={`${label}: ${cur?.name ?? value} — change`} />
      <ColourPopover anchorRef={ref} open={open} onClose={() => setOpen(false)} label={label} width={232}>
        <PresetPanel label={label} swatches={swatches} value={value} onPick={(v) => { onPick(v); setOpen(false); ref.current?.focus() }} />
      </ColourPopover>
    </>
  )
}
